import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Clock,
  Calendar,
  Timer,
  Users,
  FileText,
  Download,
  RefreshCw,
  TrendingUp,
  BarChart3
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TimeReports = () => {
  const { user } = useAuth();
  
  // State for different report types
  const [dailyReport, setDailyReport] = useState(null);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [individualReport, setIndividualReport] = useState(null);
  const [allStaff, setAllStaff] = useState([]);
  
  // UI State
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedWeekStart, setSelectedWeekStart] = useState('');
  const [selectedStaffId, setSelectedStaffId] = useState(user?.id || '');
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    // Set default week start to Monday of current week
    const today = new Date();
    const monday = new Date(today.setDate(today.getDate() - today.getDay() + 1));
    setSelectedWeekStart(monday.toISOString().split('T')[0]);
    
    fetchAllStaff();
    fetchDailyReport();
  }, []);

  const fetchAllStaff = async () => {
    try {
      const response = await axios.get(`${API}/staff`);
      setAllStaff(response.data);
    } catch (error) {
      console.error('Error fetching staff:', error);
      toast.error('Failed to load staff data');
    }
  };

  const fetchDailyReport = async (date = selectedDate) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/time-reports/daily?date=${date}`);
      setDailyReport(response.data);
    } catch (error) {
      console.error('Error fetching daily report:', error);
      toast.error('Failed to load daily report');
    } finally {
      setLoading(false);
    }
  };

  const fetchWeeklyReport = async (weekStart = selectedWeekStart) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/time-reports/weekly?week_start=${weekStart}`);
      setWeeklyReport(response.data);
    } catch (error) {
      console.error('Error fetching weekly report:', error);
      toast.error('Failed to load weekly report');
    } finally {
      setLoading(false);
    }
  };

  const fetchIndividualReport = async (staffId = selectedStaffId, startDate = dateRange.start, endDate = dateRange.end) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/time-reports/staff/${staffId}?start_date=${startDate}&end_date=${endDate}`);
      setIndividualReport(response.data);
    } catch (error) {
      console.error('Error fetching individual report:', error);
      toast.error('Failed to load individual report');
    } finally {
      setLoading(false);
    }
  };

  const formatHours = (hours) => {
    const h = Math.floor(hours);
    const m = Math.floor((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return new Date(timeString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleExportReport = (reportData, type) => {
    const dataStr = JSON.stringify(reportData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `${type}_report_${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
    
    toast.success(`${type} report exported successfully`);
  };

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
            <BarChart3 className="h-8 w-8 text-indigo-600" />
            <span>Time Reports</span>
          </h1>
          <p className="text-gray-600 mt-1">Track and analyze staff working hours</p>
        </div>
        
        <div className="mt-4 sm:mt-0 flex items-center space-x-2">
          <Badge variant="outline" className="px-3 py-1">
            {user?.role?.replace('_', ' ').toUpperCase()}
          </Badge>
        </div>
      </div>

      {/* Reports Tabs */}
      <Tabs defaultValue="daily" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="daily" className="flex items-center space-x-2">
            <Calendar className="h-4 w-4" />
            <span>Daily Reports</span>
          </TabsTrigger>
          <TabsTrigger value="weekly" className="flex items-center space-x-2">
            <TrendingUp className="h-4 w-4" />
            <span>Weekly Reports</span>
          </TabsTrigger>
          <TabsTrigger value="individual" className="flex items-center space-x-2">
            <Users className="h-4 w-4" />
            <span>Individual Reports</span>
          </TabsTrigger>
        </TabsList>

        {/* Daily Reports Tab */}
        <TabsContent value="daily" className="space-y-6">
          <Card className="card-shadow">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Calendar className="h-5 w-5 text-indigo-600" />
                <span>Daily Time Report</span>
              </CardTitle>
              <CardDescription>View staff hours for a specific day</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0">
                <div className="flex-1">
                  <label className="text-sm font-medium text-gray-700">Select Date</label>
                  <Input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="flex space-x-2">
                  <Button
                    onClick={() => fetchDailyReport(selectedDate)}
                    disabled={loading}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                    Generate Report
                  </Button>
                  {dailyReport && (
                    <Button
                      onClick={() => handleExportReport(dailyReport, 'daily')}
                      variant="outline"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Export
                    </Button>
                  )}
                </div>
              </div>

              {dailyReport && (
                <div className="space-y-4">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Total Hours</p>
                            <p className="text-2xl font-bold text-gray-900">
                              {formatHours(dailyReport.total_hours)}
                            </p>
                          </div>
                          <Timer className="h-8 w-8 text-blue-600" />
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Staff Worked</p>
                            <p className="text-2xl font-bold text-gray-900">{dailyReport.staff_count}</p>
                          </div>
                          <Users className="h-8 w-8 text-green-600" />
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Report Date</p>
                            <p className="text-lg font-semibold text-gray-900">
                              {formatDate(dailyReport.date)}
                            </p>
                          </div>
                          <Calendar className="h-8 w-8 text-purple-600" />
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Time Entries Table */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Time Entries</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {dailyReport.time_entries.length === 0 ? (
                        <p className="text-gray-500 text-center py-8">No time entries found for this date.</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left py-2">Staff Member</th>
                                <th className="text-left py-2">Clock In</th>
                                <th className="text-left py-2">Clock Out</th>
                                <th className="text-left py-2">Hours Worked</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dailyReport.time_entries.map((entry) => (
                                <tr key={entry.id} className="border-b hover:bg-gray-50">
                                  <td className="py-3">{entry.staff_name}</td>
                                  <td className="py-3">{formatTime(entry.clock_in_time)}</td>
                                  <td className="py-3">{formatTime(entry.clock_out_time)}</td>
                                  <td className="py-3">
                                    {entry.hours_worked ? formatHours(entry.hours_worked) : 'In Progress'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Weekly Reports Tab */}
        <TabsContent value="weekly" className="space-y-6">
          <Card className="card-shadow">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <TrendingUp className="h-5 w-5 text-indigo-600" />
                <span>Weekly Time Report</span>
              </CardTitle>
              <CardDescription>View staff hours for a specific week</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0">
                <div className="flex-1">
                  <label className="text-sm font-medium text-gray-700">Week Start Date (Monday)</label>
                  <Input
                    type="date"
                    value={selectedWeekStart}
                    onChange={(e) => setSelectedWeekStart(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="flex space-x-2">
                  <Button
                    onClick={() => fetchWeeklyReport(selectedWeekStart)}
                    disabled={loading}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                    Generate Report
                  </Button>
                  {weeklyReport && (
                    <Button
                      onClick={() => handleExportReport(weeklyReport, 'weekly')}
                      variant="outline"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Export
                    </Button>
                  )}
                </div>
              </div>

              {weeklyReport && (
                <div className="space-y-4">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Total Hours</p>
                            <p className="text-2xl font-bold text-gray-900">
                              {formatHours(weeklyReport.total_hours)}
                            </p>
                          </div>
                          <Timer className="h-8 w-8 text-blue-600" />
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Staff Count</p>
                            <p className="text-2xl font-bold text-gray-900">{weeklyReport.staff_count}</p>
                          </div>
                          <Users className="h-8 w-8 text-green-600" />
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Week Period</p>
                            <p className="text-sm font-semibold text-gray-900">
                              {formatDate(weeklyReport.week_start)} - {formatDate(weeklyReport.week_end)}
                            </p>
                          </div>
                          <Calendar className="h-8 w-8 text-purple-600" />
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Staff Hours Table */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Staff Hours Summary</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {Object.keys(weeklyReport.staff_hours).length === 0 ? (
                        <p className="text-gray-500 text-center py-8">No time entries found for this week.</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left py-2">Staff Member</th>
                                <th className="text-left py-2">Total Hours</th>
                                <th className="text-left py-2">Days Worked</th>
                                <th className="text-left py-2">Avg Hours/Day</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(weeklyReport.staff_hours).map(([staffId, data]) => (
                                <tr key={staffId} className="border-b hover:bg-gray-50">
                                  <td className="py-3">{data.staff_name}</td>
                                  <td className="py-3">{formatHours(data.total_hours)}</td>
                                  <td className="py-3">{data.days_worked}</td>
                                  <td className="py-3">
                                    {data.days_worked > 0 ? formatHours(data.total_hours / data.days_worked) : '0h 0m'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Individual Reports Tab */}
        <TabsContent value="individual" className="space-y-6">
          <Card className="card-shadow">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Users className="h-5 w-5 text-indigo-600" />
                <span>Individual Staff Report</span>
              </CardTitle>
              <CardDescription>View time report for a specific staff member</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Staff Member</label>
                  <Select value={selectedStaffId} onValueChange={setSelectedStaffId}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select staff member" />
                    </SelectTrigger>
                    <SelectContent>
                      {allStaff.map((staff) => (
                        <SelectItem key={staff.id} value={staff.id}>
                          {staff.full_name} ({staff.role})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Start Date</label>
                  <Input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">End Date</label>
                  <Input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="flex space-x-2">
                <Button
                  onClick={() => fetchIndividualReport(selectedStaffId, dateRange.start, dateRange.end)}
                  disabled={loading || !selectedStaffId}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  Generate Report
                </Button>
                {individualReport && (
                  <Button
                    onClick={() => handleExportReport(individualReport, 'individual')}
                    variant="outline"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export
                  </Button>
                )}
              </div>

              {individualReport && (
                <div className="space-y-4">
                  {/* Individual Report Content */}
                  <Card>
                    <CardHeader>
                      <CardTitle>
                        {individualReport.staff_name}'s Time Report
                      </CardTitle>
                      <CardDescription>
                        {formatDate(individualReport.start_date)} - {formatDate(individualReport.end_date)}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <p className="text-sm font-medium text-blue-600">Total Hours</p>
                          <p className="text-2xl font-bold text-blue-900">
                            {formatHours(individualReport.total_hours)}
                          </p>
                        </div>
                        
                        <div className="bg-green-50 p-4 rounded-lg">
                          <p className="text-sm font-medium text-green-600">Days Worked</p>
                          <p className="text-2xl font-bold text-green-900">{individualReport.days_worked}</p>
                        </div>
                        
                        <div className="bg-purple-50 p-4 rounded-lg">
                          <p className="text-sm font-medium text-purple-600">Avg Hours/Day</p>
                          <p className="text-2xl font-bold text-purple-900">
                            {individualReport.days_worked > 0 ? 
                              formatHours(individualReport.total_hours / individualReport.days_worked) : 
                              '0h 0m'
                            }
                          </p>
                        </div>
                      </div>

                      {individualReport.time_entries && individualReport.time_entries.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left py-2">Date</th>
                                <th className="text-left py-2">Clock In</th>
                                <th className="text-left py-2">Clock Out</th>
                                <th className="text-left py-2">Hours Worked</th>
                              </tr>
                            </thead>
                            <tbody>
                              {individualReport.time_entries.map((entry) => (
                                <tr key={entry.id} className="border-b hover:bg-gray-50">
                                  <td className="py-3">{formatDate(entry.date)}</td>
                                  <td className="py-3">{formatTime(entry.clock_in_time)}</td>
                                  <td className="py-3">{formatTime(entry.clock_out_time)}</td>
                                  <td className="py-3">
                                    {entry.hours_worked ? formatHours(entry.hours_worked) : 'In Progress'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-gray-500 text-center py-8">No time entries found for the selected period.</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TimeReports;